import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import { api } from "../../api";
import { 
  IoGlobe, 
  IoTrashOutline, 
  IoWarning, 
  IoCopyOutline, 
  IoCheckmarkDoneCircleOutline 
} from "react-icons/io5";

export default function Domains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "info" });

  const copyToClipboard = (text, key) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  const loadDomains = async () => {
    try {
      const res = await api.listDomains();
      if (res.success && res.data) {
        setDomains(res.data.domains);
      }
    } catch (err) {
      console.error("Failed to load domains:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDomains();
  }, []);

  const handleVerify = async (domainId) => {
    setMessage({ text: "", type: "info" });
    setVerifyingId(domainId);
    try {
      const res = await api.verifyDomain(domainId);
      if (res.success && res.data?.verified) {
        setMessage({ text: res.message || "Domain verified and SSL certificate provisioned successfully!", type: "success" });
      } else {
        setMessage({ text: res.error?.message || res.message || "DNS record not detected. Ensure proxy is disabled.", type: "error" });
      }
      await loadDomains();
    } catch (err) {
      setMessage({ text: err.message || "Verification failed. Check DNS records and remove any proxy.", type: "error" });
      await loadDomains();
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDelete = async (domainId, domainName) => {
    if (!window.confirm(`Delete custom domain "${domainName}"?`)) return;
    try {
      await api.deleteDomain(domainId);
      loadDomains();
    } catch (err) {
      alert("Failed to delete domain: " + err.message);
    }
  };

  const domColumns = [
    { name: "domain", align: "left" },
    { name: "site", align: "left" },
    { name: "ssl status", align: "center" },
    { name: "added on", align: "center" },
    { name: "actions", align: "right" },
  ];

  const domRows = domains.map((d) => {
    const isSslActive = d.sslStatus === "active" && d.verified;
    const isSslFailed = d.sslStatus === "failed";
    const isPending = !d.verified;

    return {
      domain: (
        <VuiBox>
          {isSslActive ? (
            <a href={`https://${d.domain}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ "&:hover": { color: "#0075ff" } }}>
                {d.domain} ↗
              </VuiTypography>
            </a>
          ) : (
            <VuiTypography variant="button" color="white" fontWeight="bold">
              {d.domain}
            </VuiTypography>
          )}
          {d.sslError && (
            <VuiTypography variant="caption" color="error" display="block" sx={{ maxWidth: "260px" }}>
              {d.sslError.slice(0, 80)}...
            </VuiTypography>
          )}
        </VuiBox>
      ),
      site: (
        <Link to={`/sites/${d.siteId}`} style={{ textDecoration: "none" }}>
          <VuiTypography variant="button" color="info" fontWeight="medium">
            {d.siteName} ({d.subdomain}.web.int.yt)
          </VuiTypography>
        </Link>
      ),
      "ssl status": (
        <VuiBadge
          color={isSslActive ? "success" : isSslFailed ? "error" : "warning"}
          variant="standard"
          size="sm"
          badgeContent={isSslActive ? "SSL ACTIVE & SECURE" : isSslFailed ? "SSL FAILED" : "PENDING DNS"}
        />
      ),
      "added on": (
        <VuiTypography variant="caption" color="text">
          {new Date(d.createdAt).toLocaleDateString()}
        </VuiTypography>
      ),
      actions: (
        <VuiBox display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          {(!isSslActive || isPending || isSslFailed) && (
            <VuiButton
              variant="outlined"
              color="info"
              size="small"
              disabled={verifyingId === d.id}
              onClick={() => handleVerify(d.id)}
            >
              {verifyingId === d.id ? (
                <VuiBox display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={12} color="inherit" />
                  <span>Checking...</span>
                </VuiBox>
              ) : isSslFailed ? (
                "Retry SSL"
              ) : (
                "Verify & SSL"
              )}
            </VuiButton>
          )}
          {!d.isPrimary && (
            <VuiButton
              variant="text"
              color="error"
              size="small"
              onClick={() => handleDelete(d.id, d.domain)}
            >
              <IoTrashOutline size="16px" />
            </VuiButton>
          )}
        </VuiBox>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            Custom Domains &amp; SSL
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            Connect external custom domains to your hosted static sites with automatic Let&apos;s Encrypt SSL
          </VuiTypography>
        </VuiBox>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {/* Proxy Warning Alert */}
        <VuiBox mb={3}>
          <VuiBox p={2} sx={{ backgroundColor: "rgba(255, 152, 0, 0.08)", borderRadius: "12px", border: "1px solid rgba(255, 152, 0, 0.35)" }}>
            <VuiBox display="flex" alignItems="flex-start" gap={1.5}>
              <IoWarning size="24px" color="#ff9800" style={{ flexShrink: 0, marginTop: "2px" }} />
              <VuiBox>
                <VuiTypography variant="button" color="warning" fontWeight="bold" display="block">
                  Important: Disable CDN Proxy (Cloudflare Orange Cloud) Before Verifying
                </VuiTypography>
                <VuiTypography variant="caption" color="text" display="block" mt={0.5} sx={{ lineHeight: 1.6 }}>
                  If your domain uses <strong>Cloudflare</strong> or any CDN, ensure the proxy status for your DNS record is switched to <strong>DNS Only (Grey Cloud)</strong>. Active CDN proxies interfere with Let&apos;s Encrypt HTTP-01 verification and prevent certificates from being issued.
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>
        </VuiBox>

        {/* Instructions Card with Copy Buttons */}
        <VuiBox mb={3}>
          <Card>
            <VuiBox p={1}>
              <VuiTypography variant="lg" color="white" fontWeight="bold" mb={1}>
                DNS Record Guidelines (Click to Copy)
              </VuiTypography>
              <VuiTypography variant="button" color="text" display="block" mb={2}>
                Add one of the following records at your domain registrar/DNS provider before verifying:
              </VuiTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <VuiBox p={2} sx={{ backgroundColor: "#0b1437", borderRadius: "10px", border: "1px solid rgba(0, 117, 255, 0.25)" }}>
                    <VuiTypography variant="button" color="info" fontWeight="bold" display="block" mb={1.5}>
                      Option A: CNAME Record (Subdomains e.g. blog, app)
                    </VuiTypography>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={3}>
                        <VuiTypography variant="caption" color="text" display="block">Type</VuiTypography>
                        <VuiTypography variant="button" color="white" fontWeight="bold">CNAME</VuiTypography>
                      </Grid>
                      <Grid item xs={4}>
                        <VuiTypography variant="caption" color="text" display="block">Host</VuiTypography>
                        <VuiBox display="flex" alignItems="center" gap={0.5}>
                          <code style={{ color: "#00d4ff", fontSize: "0.85rem" }}>subdomain</code>
                          <VuiButton
                            variant="text"
                            color="info"
                            size="small"
                            sx={{ minWidth: "auto", p: 0.5 }}
                            onClick={() => copyToClipboard("subdomain", "g-cname-host")}
                          >
                            {copiedKey === "g-cname-host" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                          </VuiButton>
                        </VuiBox>
                      </Grid>
                      <Grid item xs={5}>
                        <VuiTypography variant="caption" color="text" display="block">Target</VuiTypography>
                        <VuiBox display="flex" alignItems="center" gap={0.5}>
                          <code style={{ color: "#00d4ff", fontSize: "0.85rem" }}>web.int.yt</code>
                          <VuiButton
                            variant="text"
                            color="info"
                            size="small"
                            sx={{ minWidth: "auto", p: 0.5 }}
                            onClick={() => copyToClipboard("web.int.yt", "g-cname-val")}
                          >
                            {copiedKey === "g-cname-val" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                          </VuiButton>
                        </VuiBox>
                      </Grid>
                    </Grid>
                  </VuiBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <VuiBox p={2} sx={{ backgroundColor: "#0b1437", borderRadius: "10px", border: "1px solid rgba(0, 117, 255, 0.25)" }}>
                    <VuiTypography variant="button" color="info" fontWeight="bold" display="block" mb={1.5}>
                      Option B: A Record (Root / Apex Domain e.g. domain.com)
                    </VuiTypography>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={3}>
                        <VuiTypography variant="caption" color="text" display="block">Type</VuiTypography>
                        <VuiTypography variant="button" color="white" fontWeight="bold">A</VuiTypography>
                      </Grid>
                      <Grid item xs={4}>
                        <VuiTypography variant="caption" color="text" display="block">Host</VuiTypography>
                        <VuiBox display="flex" alignItems="center" gap={0.5}>
                          <code style={{ color: "#00d4ff", fontSize: "0.85rem" }}>@</code>
                          <VuiButton
                            variant="text"
                            color="info"
                            size="small"
                            sx={{ minWidth: "auto", p: 0.5 }}
                            onClick={() => copyToClipboard("@", "g-a-host")}
                          >
                            {copiedKey === "g-a-host" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                          </VuiButton>
                        </VuiBox>
                      </Grid>
                      <Grid item xs={5}>
                        <VuiTypography variant="caption" color="text" display="block">Target</VuiTypography>
                        <VuiBox display="flex" alignItems="center" gap={0.5}>
                          <code style={{ color: "#00d4ff", fontSize: "0.85rem" }}>192.255.149.52</code>
                          <VuiButton
                            variant="text"
                            color="info"
                            size="small"
                            sx={{ minWidth: "auto", p: 0.5 }}
                            onClick={() => copyToClipboard("192.255.149.52", "g-a-val")}
                          >
                            {copiedKey === "g-a-val" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                          </VuiButton>
                        </VuiBox>
                      </Grid>
                    </Grid>
                  </VuiBox>
                </Grid>
              </Grid>
            </VuiBox>
          </Card>
        </VuiBox>

        {/* Domains List */}
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Connected Custom Domains
            </VuiTypography>
            <VuiButton variant="text" color="info" size="small" onClick={loadDomains}>
              Refresh
            </VuiButton>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={8}>
              <CircularProgress color="info" />
            </VuiBox>
          ) : domains.length === 0 ? (
            <VuiBox py={8} textAlign="center">
              <IoGlobe size="48px" color="#0075ff" style={{ marginBottom: "16px" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold" mb={1}>
                No Custom Domains Attached
              </VuiTypography>
              <VuiTypography variant="button" color="text">
                Go to any site&apos;s details page to attach your custom domain.
              </VuiTypography>
            </VuiBox>
          ) : (
            <Table columns={domColumns} rows={domRows} />
          )}
        </Card>
      </VuiBox>

      <Footer />
    </DashboardLayout>
  );
}